import { message } from 'antd';
import { t } from '../../../i18n';
import apiClient from '../../../services/apiClient';

export const ok = (res) => res && res.status === 'success';
export const errMsg = (res, fallback) => (res && (res.msg || res.error)) || fallback;

/** Runs an API call and reports success/error with the backend message. Returns the response or null. */
export const runAction = async (fn, successText, failText) => {
    try {
        const res = await fn();
        if (ok(res)) {
            message.success(res.msg || successText);
            return res;
        }
        message.error(errMsg(res, failText));
        return null;
    } catch (e) {
        message.error(failText);
        return null;
    }
};

export const toArray = (d, ...keys) => {
    if (Array.isArray(d)) return d;
    for (const k of keys) {
        if (Array.isArray(d?.[k])) return d[k];
    }
    if (Array.isArray(d?.data)) return d.data;
    if (Array.isArray(d?.items)) return d.items;
    return [];
};

export const generatePassword = (length = 12) => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$%';
    let out = '';
    for (let i = 0; i < length; i++) out += chars.charAt(Math.floor(Math.random() * chars.length));
    return out;
};

export const formatMemory = (mb) => {
    const n = Number(mb) || 0;
    if (n >= 1024) return `${+(n / 1024).toFixed(1)} GB`;
    return `${n} MB`;
};

export const formatMB = (mb) => {
    const n = Number(mb) || 0;
    if (n >= 1024 * 1024) return `${(n / 1024 / 1024).toFixed(2)} TB`;
    if (n >= 1024) return `${(n / 1024).toFixed(2)} GB`;
    return `${n.toFixed(n % 1 ? 1 : 0)} MB`;
};

export const formatBps = (bps) => {
    const n = Number(bps) || 0;
    if (n >= 1024 * 1024 * 1024) return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB/s`;
    if (n >= 1024 * 1024) return `${(n / 1024 / 1024).toFixed(2)} MB/s`;
    if (n >= 1024) return `${(n / 1024).toFixed(1)} KB/s`;
    return `${Math.round(n)} B/s`;
};

export const ISO_NAME_RE = /^[A-Za-z0-9][A-Za-z0-9._-]{0,127}\.iso$/;

/** Statuses in which power / guest operations make sense. */
export const OPERABLE = ['active', 'flow_limit'];

/** Admin backup detail (/api/evm/admin/backups/vms/{id}) → the {plan, items, jobs} shape AutoBackupCard reads. */
export const normalizeAdminBackups = (r) => {
    if (!ok(r)) return r;
    const d = r.data || {};
    const jobs = toArray(d.jobs);
    const pol = d.policy;
    return {
        status: 'success',
        data: {
            plan: {
                enabled: !!d.enabled,
                source: d.source,
                policy: pol ? { ...pol, window: [pol.window_start, pol.window_end], user_restore: true } : null,
                target: d.target,
                last_success_at: d.last_success_at,
                fail_count: d.fail_count,
                manual_left: null, // admin: no daily limit
                running: jobs.find((j) => ['backup', 'restore'].includes(j.kind) && ['queued', 'running', 'cancelling'].includes(j.status)) || null,
                addon: d.addon ? { active: true } : null,
                quota: d.quota || null,
            },
            items: toArray(d.points),
            jobs,
        },
    };
};

/**
 * Small adapter that hides the differences between the user and admin EVM services.
 * Every method resolves to the usual `{status, msg, data}` envelope.
 */
export const makeAdapter = (mode, api) => {
    const base = `/api/evm/${mode}/vms`;
    const get = async (path, params) => {
        try {
            const r = await apiClient.get(path, { params });
            return r.data;
        } catch (e) {
            return { status: 'error', msg: e.response?.data?.msg || e.message };
        }
    };
    const post = async (path, data) => {
        try {
            const r = await apiClient.post(path, data);
            return r.data;
        } catch (e) {
            return { status: 'error', msg: e.response?.data?.msg || e.message };
        }
    };

    return {
        mode,
        isAdmin: mode === 'admin',
        getDetail: (id) => api.getVmDetail(id),
        getState: (id) => api.getState(id),
        /** List row carries busy/region/plan/traffic/expires_at/owner which the detail endpoint lacks. */
        getListRow: async (vm) => {
            // The detail endpoint now returns the same row the lists use.
            const d = await api.getVmDetail(vm.id);
            if (ok(d) && d.data?.row) return d.data.row;
            const kw = vm.name || vm.hostname;
            const res = mode === 'admin'
                ? await api.getVms({ keyword: kw })
                : await api.getVmList({ keyword: kw, limit: 100 });
            if (!ok(res)) return null;
            return toArray(res.data).find((r) => String(r.id) === String(vm.id)) || null;
        },
        getImages: async (vm) => {
            // Only images that this VM's node can boot (published + synced there).
            if (mode === 'admin') {
                const res = await get('/api/evm/admin/images', { per_page: 500, limit: 500, node_id: vm?.node_id ?? vm?.node?.id });
                const mem = Number(vm?.memory) || 0;
                // Same memory minimum the server enforces on reinstall (ImageRequirements).
                return (ok(res) ? toArray(res.data, 'images') : []).map((i) => (Number(i.min_memory_mb) > 0 && mem > 0 && mem < Number(i.min_memory_mb)
                    ? { ...i, unavailable: t('Needs ≥{{n}} RAM', { n: `${+(Number(i.min_memory_mb) / 1024).toFixed(1)}G` }) } : i));
            }
            const res = await get('/api/evm/user/images', { vm_id: vm?.id });
            const list = ok(res) ? toArray(res.data, 'images') : [];
            if (vm?.product_id && api.getProductImages) {
                const p = await api.getProductImages(vm.product_id);
                const allowed = ok(p) ? toArray(p.data, 'images') : [];
                if (allowed.length) {
                    const ids = new Set(allowed.map((i) => String(i.id)));
                    return list.filter((i) => ids.has(String(i.id)));
                }
            }
            return list;
        },
        getSnapshots: (id) => (mode === 'admin' ? get(`${base}/${id}/snapshots`) : api.getSnapshots(id)),
        createSnapshot: (id, disk, description) => api.createSnapshot(id, { disk_id: disk, disk, description }),
        quoteDisk: (id, sizeGb) => get(`${base}/${id}/addons/quote`, { type: 'disk', size_gb: sizeGb }),
        buyDisk: (id, sizeGb) => post(`${base}/${id}/addons/disk`, { size_gb: sizeGb }),
        // elastic upgrades (user routes only; admin has no purchase route)
        getElastic: (id) => get(`${base}/${id}/elastic`),
        quoteElastic: (id, params) => get(`${base}/${id}/elastic/quote`, params),
        buyElastic: (id, data) => post(`${base}/${id}/elastic`, data),
        disableIpv6: (id) => post(`${base}/${id}/elastic/ipv6/disable`, {}),
        releaseIpv6: (id, prefixId) => post(`${base}/${id}/ipv6/${prefixId}/release`, {}),
        buyEip: (id) => post(`${base}/${id}/eip`, {}),
        /** full stop + start so a pending config takes effect */
        coldRestart: (id) => post(`${base}/${id}/restart`, {}),
        // ISO (repository ISOs synced to the VM's node)
        getIsos: (id) => get(`${base}/${id}/isos`),
        mountIso: (id, isoId) => post(`${base}/${id}/mount`, { iso_id: isoId }),
        ejectIso: (id) => post(`${base}/${id}/eject`, {}),
        /**
         * Scheduled encrypted backups → {status, data: {plan, items, jobs}}. The user API
         * splits plan / points; the admin VM endpoint returns everything at once.
         */
        getBackups: async (id) => {
            if (mode !== 'admin') {
                const [p, pts] = await Promise.all([api.getBackupPlan(id), api.getBackupPoints(id)]);
                if (!ok(p)) return p;
                return { status: 'success', data: { plan: p.data || {}, items: ok(pts) ? toArray(pts.data, 'items') : [], jobs: ok(pts) ? toArray(pts.data?.jobs) : [] } };
            }
            return normalizeAdminBackups(await api.getBackupPlan(id));
        },
        // admin only
        getCpu: (id) => get(`${base}/${id}/cpu`),
        setCpu: (id, data) => post(`${base}/${id}/cpu`, data),
        getNodes: async () => {
            const res = await api.getNodes?.();
            return ok(res) ? toArray(res.data) : [];
        },
    };
};
